export type EnrollmentPaymentStatus =
  | 'charged'
  | 'failed'
  | 'scheduled'
  | 'not_required'
  | 'skipped';

export interface EnrollmentStudentResult {
  studentId: string;
  studentName: string;
  enrollmentStatus: 'enrolled' | 'skipped';
  paymentStatus: EnrollmentPaymentStatus;
  paymentFailureReason?: string;
}

export interface AddStudentToEntityResponse {
  status: number;
  message: string;
  url?: string;
  clientSecret?: string;
  stripePublicApiKey?: string;
  /** Stripe Connect: connected account ID (acct_xxx) for operating on behalf of studio */
  stripeAccount?: string;
  existingEnrollmentsList?: string[];
  results?: EnrollmentStudentResult[];
  summary?: {
    total: number;
    enrolled: number;
    skipped: number;
    paymentFailed: number;
  };
}

export type AddStudentToEntityOutcome =
  | { type: 'success'; message: string }
  | { type: 'partial'; message: string }
  | { type: 'already_enrolled'; message: string }
  | { type: 'error'; message: string };

/**
 * Maps add-student-to-entity API payload to toast outcome.
 * Payment can fail (e.g. Cash App) while enrollment still succeeds.
 */
export const getAddStudentToEntityOutcome = (
  data: AddStudentToEntityResponse,
): AddStudentToEntityOutcome => {
  const results = data.results ?? [];
  const enrolled = results.filter(
    (result) => result.enrollmentStatus === 'enrolled',
  );
  const paymentFailedFromResults = enrolled.filter(
    (result) => result.paymentStatus === 'failed',
  );
  const paymentFailedCount =
    data.summary?.paymentFailed ?? paymentFailedFromResults.length;
  const enrolledCount = data.summary?.enrolled ?? enrolled.length;
  const alreadyEnrolled = data.existingEnrollmentsList ?? [];

  if (
    alreadyEnrolled.length > 0 &&
    enrolledCount === 0 &&
    paymentFailedCount === 0
  ) {
    return {
      type: 'already_enrolled',
      message:
        data.message ||
        `Student already enrolled: ${alreadyEnrolled.join(', ')}`,
    };
  }

  // ENR-3580: paymentFailedCount === 0 with enrolledCount === 0 used to toast success.
  if (enrolledCount === 0) {
    return {
      type: 'error',
      message:
        data.message || 'No students were enrolled. Nothing was charged.',
    };
  }

  if (paymentFailedCount === 0) {
    return {
      type: 'success',
      message: data.message || 'Students enrolled successfully!',
    };
  }

  const reason = paymentFailedFromResults[0]?.paymentFailureReason;
  if (enrolledCount > 0 && paymentFailedCount >= enrolledCount) {
    return {
      type: 'error',
      message:
        reason ||
        data.message ||
        'Students enrolled, but payment failed. Ask the family to update their payment method.',
    };
  }

  return {
    type: 'partial',
    message:
      data.message ||
      `${paymentFailedCount} of ${enrolledCount} enrollments had payment failures${
        reason ? `: ${reason}` : ''
      }`,
  };
};
